/** Warm-up sequence message types and helpers. */

export const STORAGE_KEY = "warm_up_sequences_draft_v2";
const LEGACY_STORAGE_KEY = "warm_up_sequences_draft_v1";

export const MESSAGE_TYPES = [
  { value: "general", label: "General" },
  { value: "weather", label: "Weather" },
];

export const WEATHER_FORECAST_MERGE_TAG = "{weather_forecast}";

export const WEATHER_SEND_MODES = [
  {
    value: "regardless",
    label: "Send regardless of weather",
  },
  {
    value: "non_ideal_only",
    label: "Send only when weather conditions are not ideal",
  },
];

const VALID_WEATHER_MODES = new Set(WEATHER_SEND_MODES.map((m) => m.value));

export const DELAY_ANCHORS = [
  {
    value: "before_appointment",
    label: "Days before appointment",
    delayLabel: "Delay (days before visit)",
    helper: (n) =>
      n === 0
        ? "Send on the day of the scheduled visit."
        : `Send ${n} day${n === 1 ? "" : "s"} before the scheduled visit.`,
  },
  {
    value: "after_booking_created",
    label: "Days after booking was created",
    delayLabel: "Delay (days after booking)",
    helper: (n) =>
      n === 0
        ? "Send on the day the calendar booking was created."
        : `Send ${n} day${n === 1 ? "" : "s"} after the calendar booking was created.`,
  },
];

const VALID_DELAY_ANCHORS = new Set(DELAY_ANCHORS.map((a) => a.value));
const DEFAULT_DELAY_ANCHOR = "before_appointment";

export function getDelayAnchorMeta(anchorValue) {
  return DELAY_ANCHORS.find((a) => a.value === anchorValue) ?? DELAY_ANCHORS[0];
}

export const createEmptyWarmUpMessage = () => ({
  id:
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  message_type: "general",
  delay_days: 0,
  delay_anchor: DEFAULT_DELAY_ANCHOR,
  text: "",
  enabled: true,
  ai_corrected: false,
  weather_send_mode: "regardless",
});

export const DEFAULT_WARM_UP_MESSAGES = [];

export function normalizeWarmUpMessage(m) {
  const base = createEmptyWarmUpMessage();
  const weatherMode = m?.weather_send_mode;
  return {
    id: m?.id || base.id,
    message_type: MESSAGE_TYPES.some((t) => t.value === m?.message_type)
      ? m.message_type
      : "general",
    delay_days: Math.max(0, Number(m?.delay_days) || 0),
    delay_anchor: VALID_DELAY_ANCHORS.has(m?.delay_anchor)
      ? m.delay_anchor
      : DEFAULT_DELAY_ANCHOR,
    text: typeof m?.text === "string" ? m.text : "",
    enabled: m?.enabled !== false,
    ai_corrected: m?.ai_corrected === true,
    weather_send_mode: VALID_WEATHER_MODES.has(weatherMode)
      ? weatherMode
      : "regardless",
  };
}

export function normalizeWarmUpMessages(messages) {
  if (!Array.isArray(messages)) return [];
  return messages.map(normalizeWarmUpMessage);
}

/** One-time migration: read legacy localStorage draft only. */
export function loadLegacyWarmUpDraftFromLocalStorage() {
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    }
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return normalizeWarmUpMessages(parsed);
  } catch {
    return null;
  }
}

export function clearWarmUpLocalStorageDraft() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function getWarmUpSequencesApiUrl(backendUrl) {
  const base = (backendUrl || "").replace(/\/$/, "");
  return `${base}/api/warm-up-sequences`;
}
